/* This file is auto-generated by 'tools/data-lib-gen'. Please don't edit it manually. */
import fetch from 'cross-fetch';
import { ApiError } from './ApiError';
import { ApiRequestOptions } from './ApiRequestOptions';
import { ApiResult } from './ApiResult';
import { CancelablePromise, OnCancel } from './CancelablePromise';
import { camelCase, snakeCase } from 'change-case';

const IS_SERVER = typeof window === 'undefined';

const DEFAULT_CONFIG: RequestConfig = { snakeCaseOnServer: true };

const getEnvVar = (key: string) => {
  return IS_SERVER
    ? typeof process !== 'undefined'
      ? process.env[key]
      : // @ts-ignore
        global.ENV[key]
    : // @ts-ignore
      global.ENV[key];
};

// const IS_DEV =
//   IS_SERVER &&
//   // eslint-disable-next-line @typescript-eslint/dot-notation
//   process.env['NODE_ENV'] === 'development';

export interface RequestConfig {
  base?: string;
  headers?: Headers | Record<string, string>;
  token?: string;
  refreshToken?: string;
  credentials?: RequestCredentials;
  snakeCaseOnServer?: boolean;
}

const isDefined = <T>(
  value: T | null | undefined
): value is Exclude<T, null | undefined> => {
  return value !== undefined && value !== null;
};

const isString = (value: any): value is string => {
  return typeof value === 'string';
};

const isStringWithValue = (value: any): value is string => {
  return isString(value) && value !== '';
};

const isBlob = (value: any): value is Blob => {
  return (
    typeof value === 'object' &&
    typeof value.type === 'string' &&
    typeof value.stream === 'function' &&
    typeof value.arrayBuffer === 'function' &&
    typeof value.constructor === 'function' &&
    typeof value.constructor.name === 'string' &&
    /^(Blob|File)$/.test(value.constructor.name) &&
    /^(Blob|File)$/.test(value[Symbol.toStringTag])
  );
};

const isFormData = (value: any): value is FormData => {
  return value instanceof FormData;
};

const getQueryString = (params: Record<string, any>): string => {
  const qs: string[] = [];

  const append = (key: string, value: any) => {
    qs.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
  };

  const process = (key: string, value: any) => {
    if (isDefined(value)) {
      if (Array.isArray(value)) {
        for (const v of value) {
          process(key + '[]', v);
        }
      } else if (typeof value === 'object') {
        for (const [k, v] of Object.entries(value)) {
          process(`${key}[${k}]`, v);
        }
      } else {
        append(key, value);
      }
    }
  };

  const snakeCasedQueryString = recursiveKeyMap(params, key => snakeCase(key));

  for (const [key, value] of Object.entries(snakeCasedQueryString)) {
    process(key, value);
  }

  if (qs.length > 0) {
    return `?${qs.join('&')}`;
  }

  return '';
};

const getUrl = (config: RequestConfig, options: ApiRequestOptions): string => {
  const encoder = encodeURI;

  const path = options.url.replace(
    /{(.*?)}/g,
    (substring: string, group: string) => {
      if (options.path && group in options.path) {
        return encoder(String(options.path[group]));
      }

      return substring;
    }
  );

  const url = config.base?.replace(/\/$/, '') + path;

  if (options.query) {
    return `${url}${getQueryString(options.query)}`;
  }
  return url;
};

const getFormData = (options: ApiRequestOptions): FormData | undefined => {
  if (options.formData) {
    const formData = new FormData();

    const process = (key: string, value: any) => {
      if (isString(value) || isBlob(value)) {
        formData.append(snakeCase(key), value);
      } else {
        formData.append(snakeCase(key), JSON.stringify(value));
      }
    };

    for (const [key, value] of Object.entries(options.formData).filter(
      ([_, value]) => isDefined(value)
    )) {
      if (Array.isArray(value)) {
        for (const v of value) {
          process(key, v);
        }
      } else {
        process(key, value);
      }
    }

    return formData;
  }
  return undefined;
};

type Resolver<T> = (options: ApiRequestOptions) => Promise<T>;

const resolve = async <T>(
  options: ApiRequestOptions,
  resolver?: T | Resolver<T>
): Promise<T | undefined> => {
  if (typeof resolver === 'function') {
    return (resolver as Resolver<T>)(options);
  }
  return resolver;
};

const getHeaders = async (
  config: RequestConfig,
  options: ApiRequestOptions
): Promise<Record<string, string>> => {
  const token = await resolve(options, config.token);
  let configHeaders: any = await resolve(options, config.headers);
  const additionalHeaders: Record<string, string> = {};

  const cookie = configHeaders
    ? 'get' in configHeaders
      ? configHeaders.get('cookie')
      : configHeaders['cookie']
    : undefined;

  if (cookie) {
    additionalHeaders['cookie'] = cookie;
  }

  const specialHeaders = {
    ...(getEnvVar('PUBLIC_CF_ACCESS_CLIENT_ID')
      ? {
          'CF-Access-Client-Id': getEnvVar('PUBLIC_CF_ACCESS_CLIENT_ID'),
          'CF-Access-Client-Secret': getEnvVar(
            'PRIVATE_CF_ACCESS_CLIENT_SECRET'
          )
        }
      : {})
  };

  const headers = Object.entries({
    ...specialHeaders,
    Accept: 'application/json',
    ...options.headers,
    ...additionalHeaders
  })
    .filter(([_, value]) => isDefined(value))
    .reduce<Record<string, string>>(
      (headers, [key, value]) => ({
        ...headers,
        [key]: String(value)
      }),
      {}
    );

  if (isStringWithValue(token)) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  if (options.body) {
    if (options.mediaType) {
      headers['Content-Type'] = options.mediaType;
    } else if (isBlob(options.body)) {
      headers['Content-Type'] = options.body.type || 'application/octet-stream';
    } else if (isString(options.body)) {
      headers['Content-Type'] = 'text/plain';
    } else if (!isFormData(options.body)) {
      headers['Content-Type'] = 'application/json';
    }
  }

  return headers;
};

const isPrimitive = (value: any) => !(value instanceof Object);

const recursiveKeyMap = <T extends Record<string, unknown>>(
  obj: any,
  fn: (key: string) => string
): T => {
  if (Array.isArray(obj)) {
    return obj.map((v: any) => recursiveKeyMap(v, fn)) as unknown as T;
  }

  if (isPrimitive(obj)) {
    return obj;
  }

  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => {
      const getValue = (value: any): any =>
        typeof value === 'object' && value !== null
          ? recursiveKeyMap(value, fn)
          : value;

      return [
        fn(key),
        Array.isArray(value) ? value.map(val => getValue(val)) : getValue(value)
      ];
    })
  ) as T;
};

const serializeBodyObject = (body: any, config: RequestConfig) => {
  if (!config.snakeCaseOnServer) {
    return body;
  }

  return recursiveKeyMap(body, key => snakeCase(key));
};

const deserializeBodyObject = (body: any) => {
  return recursiveKeyMap(body, key => camelCase(key));
};

const getRequestBody = (
  config: RequestConfig,
  options: ApiRequestOptions
): any => {
  if (options.body) {
    if (options.mediaType?.includes('/json')) {
      return JSON.stringify(serializeBodyObject(options.body, config));
    } else if (
      isString(options.body) ||
      isBlob(options.body) ||
      isFormData(options.body)
    ) {
      return options.body;
    } else {
      return JSON.stringify(serializeBodyObject(options.body, options));
    }
  }
  return undefined;
};

export const sendRequest = async (
  config: RequestConfig,
  options: ApiRequestOptions,
  url: string,
  body: any,
  formData: FormData | undefined,
  headers: Record<string, string>,
  onCancel: OnCancel
): Promise<Response> => {
  const controller = new AbortController();

  // Remove trailing slash from base url
  config.base = config.base?.replace(/\/$/, '');

  const request: RequestInit = {
    headers,
    body: body ?? formData,
    method: options.method,
    signal: controller.signal
  };

  if (config.credentials) {
    request.credentials = config.credentials;
  }

  onCancel(() => controller.abort());

  const originalResponse = await fetch(url, request);

  if (IS_SERVER) {
    const responseHeaders = originalResponse.headers;

    const setCookieHeader = responseHeaders.get('Set-Cookie');

    if (setCookieHeader && config.headers) {
      if ('append' in config.headers) {
        (config.headers as Headers).append('Set-Cookie', setCookieHeader);
      } else {
        config.headers['Set-Cookie'] = setCookieHeader;
      }
    }
  }

  return originalResponse;
};

const getResponseBody = async (
  response: Response,
  options: ApiRequestOptions
): Promise<any> => {
  if (response.status !== 204) {
    const contentType = response.headers.get('Content-Type');

    if (contentType) {
      const isJSON = contentType.toLowerCase().startsWith('application/json');

      return await (isJSON
        ? response.json().then(json => deserializeBodyObject(json))
        : response.text());
    }
  }

  return undefined;
};

const catchErrorCodes = (
  options: ApiRequestOptions,
  result: ApiResult
): void => {
  const errors: Record<number, string> = {
    400: 'Bad Request',
    401: 'Unauthorized',
    403: 'Forbidden',
    404: 'Not Found',
    500: 'Internal Server Error',
    502: 'Bad Gateway',
    503: 'Service Unavailable',
    ...options.errors
  };

  // Auto throw 404 error on server side
  if (result.status === 404 && IS_SERVER) {
    throw new Response('Not Found', {
      status: 404
    });
  }

  const error = errors[result.status];

  if (error) {
    throw new ApiError(options, result, error);
  }

  if (!result.ok) {
    throw new ApiError(options, result, 'Generic Error');
  }
};

/**
 * Request method
 * @param config The configuration object
 * @param options The request options from the service
 * @returns CancelablePromise<T>
 * @throws ApiError
 */
export const request = <T>(
  config: RequestConfig | undefined,
  options: ApiRequestOptions
): CancelablePromise<T> => {
  return new CancelablePromise(async (resolve, reject, onCancel) => {
    try {
      config = {
        ...DEFAULT_CONFIG,
        ...(config ?? {})
      };

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      /* @ts-ignore */
      if (!config.base) {
        config.base =
          typeof window !== 'undefined'
            ? getEnvVar('PUBLIC_API_URL')
            : getEnvVar('PRIVATE_SERVER_SIDE_API_URL');
      }

      if (!IS_SERVER && !config.token && (window as any).__userSession) {
        config.token = (window as any).__userSession.token;
      }

      if (!config.base) {
        throw new Error('@kratos/data: Missing base url');
      }

      const url = getUrl(config, options);
      const formData = getFormData(options);
      const body = getRequestBody(config, options);
      const headers = await getHeaders(config, options);

      if (!onCancel.isCancelled) {
        const response = await sendRequest(
          config,
          options,
          url,
          body,
          formData,
          headers,
          onCancel
        );

        const responseBody = await getResponseBody(response, options);

        const result: ApiResult = {
          url,
          ok: response.ok,
          status: response.status,
          statusText: response.statusText,
          body: responseBody
        };

        catchErrorCodes(options, result);
        resolve(result.body);
      }
    } catch (error) {
      reject(error);
    }
  });
};
